/**
 * Organisation-driven reporting-currency resolution.
 *
 * Plugin (and host) surfaces render reporting-currency amounts in whatever the
 * organisation has configured as its reporting currency, without threading a
 * currency code through every call site. This module holds the module-level
 * reporting-currency ref, its setter, and the readers with a deterministic
 * fallback constant. It is pure — no React, no host access — so it lives in
 * core-utils behind the `@ethisyscore/core-utils/money` sub-path, mirroring the
 * date org-format seam (`@ethisyscore/core-utils/date/org-format`).
 *
 * The ref is stamped by the surface sync hook — the SDK
 * `useReportingCurrencySync` reads the host
 * `settings:get-organisation-reporting-currency` tool and calls
 * `setOrgReportingCurrency`. When no reporting currency is stamped (a
 * standalone/mock run, an old host, or a failed fetch) resolution falls back to
 * the deterministic UK default `GBP`, NOT the OS locale, so output is
 * machine-stable regardless of the host. This is the exact fail-open contract of
 * the date seam's UK `dd/MM/yyyy` fallback.
 *
 * Deciding WHICH currency an amount is in (its own currency vs the org's
 * reporting currency) and CONVERTING between currencies stay host-aware concerns
 * of the plugin-ui `useCurrency` hook. This seam only answers "what is the org's
 * reporting currency" for the pure {@link formatMoney} default path.
 */

/**
 * Deterministic fallback reporting currency when no org currency is stamped.
 * Matches the date seam's UK fallback (`ORG_DATE_FALLBACK_FORMAT`) so money and
 * dates degrade consistently, and so output does not drift with the OS locale of
 * whatever host renders it.
 */
export const MONEY_FALLBACK_CURRENCY = "GBP";

/** The organisation's reporting currency as stamped by the surface sync hook. */
export interface OrgReportingCurrency {
  /** ISO 4217 code (e.g. `"GBP"`, `"EUR"`) drives `Intl` currency formatting. */
  code: string;
  /**
   * Host decimal-places override for the currency, or null to use the currency's
   * ISO default (GBP -> 2, JPY -> 0). Applied by {@link formatMoney} only when the
   * caller supplies no explicit decimalPlaces.
   */
  decimalPlaces: number | null;
}

// ---------------------------------------------------------------------------
// Module-level reporting-currency ref
// ---------------------------------------------------------------------------

let orgReportingCurrencyRef: OrgReportingCurrency | null = null;

/**
 * Stamps the module-level reporting-currency ref. Called by the surface sync hook
 * each time the organisation's reporting currency resolves. Pass `null` to clear
 * it and fall back to the deterministic {@link MONEY_FALLBACK_CURRENCY}.
 */
export function setOrgReportingCurrency(currency: OrgReportingCurrency | null): void {
  orgReportingCurrencyRef = currency;
}

/** The org's stamped reporting currency, or null to fall back to the UK default. */
export function getOrgReportingCurrency(): OrgReportingCurrency | null {
  return orgReportingCurrencyRef;
}

/**
 * The org's effective reporting-currency code: the stamped code when present, else
 * the deterministic {@link MONEY_FALLBACK_CURRENCY} (`GBP`). Never null, so callers
 * always have a well-formed code to format with.
 */
export function resolveOrgReportingCurrencyCode(): string {
  return orgReportingCurrencyRef?.code ?? MONEY_FALLBACK_CURRENCY;
}
